import { Link } from "react-router-dom";
import { Clipboard } from "lucide-react";
import { Card } from "../../../components/Card";
import { Chip } from "../../../components/Chip";
import { IconButton } from "../../../components/IconButton";
import { ReadinessDial } from "../../../components/ReadinessDial";
import { StatusBadge } from "../../../components/StatusBadge";
import type { JobPost } from "../../../types";

function formatBudget(job: JobPost): string {
  if (job.budgetMin !== undefined && job.budgetMax !== undefined) {
    return `$${job.budgetMin} to $${job.budgetMax}`;
  }
  if (job.budgetMin !== undefined) {
    return `From $${job.budgetMin}`;
  }
  if (job.budgetMax !== undefined) {
    return `Up to $${job.budgetMax}`;
  }
  return "Not listed";
}

export function JobCard({ job }: { job: JobPost }) {
  const summary = `${job.title}\n${job.summary}\n${job.neighborhood}, ${job.city}\n${formatBudget(job)}`;
  const visibleFlags = job.riskFlags.slice(0, 2);
  const remainingFlags = job.riskFlags.length - visibleFlags.length;
  const footer = (
    <div className="flex flex-wrap gap-2">
      {visibleFlags.length ? (
        <>
          {visibleFlags.map((flag) => (
            <Chip
              key={flag.type}
              tone={
                flag.severity === "RED"
                  ? "danger"
                  : flag.severity === "AMBER"
                    ? "warning"
                    : flag.severity === "GREEN"
                      ? "success"
                      : "neutral"
              }
            >
              {flag.label}
            </Chip>
          ))}
          {remainingFlags > 0 ? <Chip tone="neutral">+{remainingFlags} more</Chip> : null}
        </>
      ) : (
        <Chip tone="success">No warnings found</Chip>
      )}
    </div>
  );

  return (
    <Card
      className="group flex h-full flex-col gap-5"
      footer={footer}
      interactive
      padding="lg"
      variant="surface"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="cell-label text-accent-700">{job.category.replace(/_/g, " ")}</p>
            <StatusBadge level={job.readiness.level} size="xs" tone="glyph" />
          </div>
          <h2 className="mt-3 font-sans text-2xl font-semibold leading-tight text-text-strong">
            <Link className="hover:text-accent-700" to={`/jobs/${job.id}`}>
              {job.title}
            </Link>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <ReadinessDial
            label="Detail"
            level={job.readiness.level}
            score={job.readiness.score}
            size="sm"
          />
          <IconButton
            className="opacity-40 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            icon={<Clipboard aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
            label={`Copy summary for ${job.title}`}
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.preventDefault();
              void navigator.clipboard?.writeText(summary);
            }}
          />
        </div>
      </div>
      <Link className="contents" to={`/jobs/${job.id}`}>
        <p className="line-clamp-2 text-pretty text-sm leading-6 text-text-muted">{job.summary}</p>
      </Link>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-4 text-sm">
        {[
          ["Location", job.neighborhood],
          ["Window", job.desiredWindow],
          ["Urgency", job.urgency],
          ["Budget", formatBudget(job)],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="cell-label">{label}</dt>
            <dd className={`mt-1 font-semibold text-text ${label === "Budget" ? "tabular" : ""}`}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
